import React from 'react';
import { Redirect, Route as BaseRoute } from 'react-router-dom';
import { parse as parseQueryString } from 'qs';

import LoadingIndicator from './components/LoadingIndicator';
import NavBar from './containers/NavBar';
import authLib from './lib/auth';
import { appContainer, Subscribe } from './contexts/app';

import Login from './pages/Login';
import Signup from './pages/Signup';
import PasswordForgot from './pages/PasswordForgot';
import PasswordReset from './pages/PasswordReset';
import EmailReset from './pages/EmailReset';
import AcceptInvitation from './pages/AcceptInvitation';
import MapPage from './pages/Map';
import Facility from './pages/Facility';
import MarkerTypes from './pages/MarkerTypes';
import Account from './pages/Account';
import Welcome from './pages/Welcome';

export const Route = props => {
  props.location.query = parseQueryString(props.location.search, {
    ignoreQueryPrefix: true
  });
  return <BaseRoute {...props} />;
};

export class PreppedRoute extends React.Component {
  async componentDidMount() {
    if (!this.props.appReady) {
      await this.props.prepApp();
    }
  }

  render() {
    if (!this.props.appReady) {
      return <LoadingIndicator />;
    }
    return (
      <Subscribe
        to={[appContainer]}
        render={app => {
          const permissions = app.state.getIn([
            'currentUser',
            'permissions',
            'edit'
          ]);
          const { requiredPermission, appReady } = this.props;
          if (!requiredPermission) {
            return (
              <div>
                <NavBar {...this.props._props} appReady={appReady} />
                <this.props.component {...this.props._props} />
              </div>
            );
          } else if (permissions[requiredPermission]) {
            return (
              <div>
                <NavBar {...this.props._props} appReady={appReady} />
                <this.props.component {...this.props._props} />
              </div>
            );
          } else {
            return <Redirect to="/" />;
          }
        }}
      />
    );
  }
}

export const PrivateRoute = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={props => {
      if (authLib.getToken()) {
        return (
          <PreppedRoute
            appReady={rest.appReady}
            prepApp={rest.prepApp}
            requiredPermission={rest.requiredPermission}
            _props={props}
            component={Component}
          />
        );
      } else {
        return (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: props.location }
            }}
          />
        );
      }
    }}
  />
);

export function renderPrivateRoutes(useAll, prepApp, appReady) {
  return privateRoutes.map((route, index) => {
    if (!(route.avail_expired || useAll)) {
      return null;
    }
    return (
      <PrivateRoute
        key={route.key || index}
        exact
        path={route.path}
        component={route.component}
        prepApp={prepApp}
        appReady={appReady}
        requiredPermission={route.requiredPermission}
      />
    );
  });
}

export function renderRoutes({ active, prepApp, appReady }) {
  const defaultRoute = active ? (
    <Route key="map" render={() => <Redirect to="/map" />} />
  ) : (
    <Route key="account" render={() => <Redirect to="/account/settings" />} />
  );

  return [
    ...publicRoutes.map((route, index) => (
      <Route
        key={route.key || index}
        exact
        path={route.path}
        component={route.component}
      />
    )),
    ...renderPrivateRoutes(active, prepApp, appReady),
    defaultRoute
  ];
}

export const privateRoutes = [
  // Welcome
  {
    path: '/welcome',
    component: Welcome,
    key: 'welcome',
    avail_expired: false
  },

  // Current user account
  {
    path: '/account',
    component: Account,
    key: 'account',
    avail_expired: true
  },

  // Account Settings
  {
    path: '/account/settings',
    component: Account,
    key: 'account',
    requiredPermission: 'account',
    avail_expired: true
  },

  // Edit other users
  {
    path: '/account/users',
    component: Account,
    key: 'account',
    requiredPermission: 'users',
    avail_expired: false
  },
  {
    path: '/account/users/:userId',
    component: Account,
    key: 'account',
    requiredPermission: 'users',
    avail_expired: false
  },

  // Send invitations (basically the same as users)
  {
    path: '/account/invitations',
    component: Account,
    key: 'account',
    requiredPermission: 'users'
  },
  {
    path: '/account/invitations/:invitationId',
    component: Account,
    key: 'account',
    requiredPermission: 'users'
  },

  // Send user feedback
  {
    path: '/account/feedback',
    component: Account,
    key: 'account',
    requiredPermission: 'users'
  },
  {
    path: '/account/groups',
    component: Account,
    key: 'account',
    requiredPermission: 'users'
  },

  // The main index map view
  {
    path: '/map/:facilityId/buildings/:buildingId/floors/:floorId',
    component: MapPage,
    key: 'map'
  },
  {
    path: '/map/:facilityId/buildings/:buildingId',
    component: MapPage,
    key: 'map'
  },
  {
    path: '/map/:facilityId',
    component: MapPage,
    key: 'map'
  },
  {
    path: '/map',
    component: MapPage,
    key: 'map'
  },

  {
    path: '/facilities/:facilityId/',
    component: Facility,
    key: 'facility',
    requiredPermission: 'resources'
  },
  {
    path:
      '/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers/:markerId/infographics',
    component: Facility,
    key: 'facility',
    requiredPermission: 'resources'
  },
  {
    path:
      '/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers/:markerId',
    component: Facility,
    key: 'facility',
    requiredPermission: 'resources'
  },
  {
    path:
      '/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers/',
    component: Facility,
    key: 'facility',
    requiredPermission: 'resources'
  },
  {
    path: '/facilities/:facilityId/buildings/:buildingId/floors/:floorId',
    component: Facility,
    key: 'facility',
    requiredPermission: 'resources'
  },
  {
    path: '/facilities/:facilityId/buildings/:buildingId/floors',
    component: Facility,
    key: 'facility',
    requiredPermission: 'resources'
  },
  {
    path: '/facilities/:facilityId/buildings/:buildingId',
    component: Facility,
    key: 'facility',
    requiredPermission: 'resources'
  },
  {
    path: '/facilities/:facilityId/buildings/',
    component: Facility,
    key: 'facility',
    requiredPermission: 'resources'
  },

  {
    path: '/marker-types',
    component: MarkerTypes,
    key: 'markerTypes',
    requiredPermission: 'resources'
  },
  {
    path: '/marker-types/:markerTypeId',
    component: MarkerTypes,
    key: 'markerTypes',
    requiredPermission: 'resources'
  }
];

export const publicRoutes = [
  {
    path: '/login',
    component: Login,
    key: 'login'
  },
  {
    path: '/signup',
    component: Signup,
    key: 'signup'
  },
  {
    path: '/forgot-password',
    component: PasswordForgot,
    key: 'PasswordForgot'
  },
  {
    path: '/reset-password',
    component: PasswordReset,
    key: 'PasswordReset'
  },
  {
    path: '/reset-email',
    component: EmailReset,
    key: 'EmailReset'
  },
  {
    path: '/accept-invitation/:invitationId',
    component: AcceptInvitation,
    key: 'accept-invitation'
  }
];



// WEBPACK FOOTER //
// ./src/routing.js